import React, { useEffect, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { acceptInvite, previewInvite } from "../../api/onboarding";
import { useNotify } from "../../utils/useNotify";

export default function AcceptInvite() {
  const notify = useNotify();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [username, setUsername] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("Missing invitation token.");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const p = await previewInvite(token);
        if (!cancelled) setPreview(p);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail || e?.message || "Invalid invitation."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await acceptInvite({
        token,
        password: password || undefined,
        firstname: firstname || undefined,
        lastname: lastname || undefined,
        username: username || undefined,
      });
      notify.success(
        "You are in. Sign in with this email and your password to open your farm setup."
      );
      window.setTimeout(() => {
        window.location.href = "/login";
      }, 800);
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || "Could not accept invite."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", pt: 4, px: 2 }}>
      <Card sx={{ maxWidth: 480, width: "100%" }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Invitation
          </Typography>
          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
              <CircularProgress size={28} />
              <Typography>Loading…</Typography>
            </Box>
          )}
          {!loading && preview && (
            <>
              {preview.revoked ? (
                <Alert severity="error">
                  This invitation was revoked. Ask your administrator to send a
                  new invitation.
                </Alert>
              ) : preview.expired ? (
                <Alert severity="warning">This invitation has expired.</Alert>
              ) : (
                <>
                  <Typography paragraph>
                    Join <strong>{preview.tenant_name}</strong> as{" "}
                    <strong>{preview.role_name}</strong> ({preview.email})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    New here? Set a password, your name, and username (required).
                    If we already created a placeholder account for you, you must
                    choose a password here to continue. Then sign in to finish
                    farm setup.
                  </Typography>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {typeof error === "string" ? error : JSON.stringify(error)}
                    </Alert>
                  )}
                  <form onSubmit={onSubmit}>
                    <TextField
                      label="Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      fullWidth
                      margin="normal"
                      required
                      helperText="Required — set the password you will use to sign in"
                    />
                    <TextField
                      label="First name"
                      value={firstname}
                      onChange={(e) => setFirstname(e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                    <TextField
                      label="Last name"
                      value={lastname}
                      onChange={(e) => setLastname(e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                    <TextField
                      label="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{ mt: 2 }}
                      disabled={submitting}
                    >
                      {submitting ? "Saving…" : "Accept invitation"}
                    </Button>
                  </form>
                </>
              )}
            </>
          )}
          {!loading && !preview && error && (
            <Alert severity="error">{error}</Alert>
          )}
          <Button component={RouterLink} to="/login" sx={{ mt: 2 }}>
            Sign in
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
